// Crayo, /checkout/:sessionId and /download/:orderId
//
// OVERNIGHT MIGRATION NOTE (2026-04-24):
// The fake card form used to live here. It is gone. /checkout/:sid now
// immediately calls POST /api/session/:sid/checkout and redirects to
// Stripe's hosted Checkout page. Reasons:
//   1. PCI scope, we never want card numbers in our DOM.
//   2. Stripe Checkout handles 3DS/SCA automatically, which RO cards
//      frequently require.
//   3. Fewer UI surfaces to maintain; the summary is re-shown on the
//      hosted page.
//
// /download/:oid polls GET /api/order/:oid until `status === 'paid'`,
// then shows the real signed URL as a download button. `status: 'pending'`
// renders the "your PDF is being assembled" state; `status: 'failed'`
// renders the refund-requested copy.

import React from 'react';

import { getOrder, startCheckout, ApiError } from '../src/api.js';
import { trackPixelEvent } from '../src/meta-pixel-init.js';
import { gaTrackEvent } from '../src/ga4-init.js';

// Shared.jsx defines Icon/Page/navigate/useToast. CreateFlow.jsx
// defines formatPrice + priceForPages on window. Both already loaded
// when this module runs (see src/main.jsx load order).
const { Icon, Page, navigate, useToast } = window;

// Generate a dedup id shared between the browser Pixel InitiateCheckout
// event and the server-side CAPI Purchase event. crypto.randomUUID is
// supported on every browser we care about (https-only); we fall back
// to a Math.random concat for the rare http+old combo so the rest of
// the system never sees an undefined id.
function newCheckoutEventId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'evt_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const { useState: useStateK, useEffect: useEffectK, useRef: useRefK } = React;

// --- /checkout/:sessionId ---
// No form. We kick off Stripe Checkout and navigate away; if something
// breaks (network, already-paid session, expired session) we fall back
// to a friendly error screen with a retry button.
function CheckoutPage({ sessionId }) {
  const toast = useToast();
  const [state, setState] = useStateK('starting'); // starting | failed
  const [errMessage, setErrMessage] = useStateK('');

  useEffectK(() => {
    let cancelled = false;
    const go = async () => {
      try {
        // Dedup id shared with the server-side CAPI Purchase event. The
        // backend stores it on Stripe Checkout metadata so the webhook
        // can forward the same id; Meta then dedupes browser + server
        // events. trackPixelEvent silently no-ops when consent is not
        // granted or the Pixel ID env var is unset.
        const eventId = newCheckoutEventId();
        trackPixelEvent('InitiateCheckout', { content_type: 'product' }, eventId);
        // GA4 mirror — same event_id passed as transaction_id placeholder
        // so funnel reports can join begin_checkout to purchase later.
        gaTrackEvent('begin_checkout', { transaction_id: eventId, currency: 'RON' });
        const { checkoutUrl } = await startCheckout(sessionId, eventId);
        if (cancelled) return;
        if (!checkoutUrl) throw new ApiError('checkout.bad_response', 'Nu am primit linkul de plată. Reîncearcă.');
        window.location.assign(checkoutUrl);
      } catch (err) {
        if (cancelled) return;
        setErrMessage((err && err.message) || 'Nu am putut porni plata.');
        setState('failed');
      }
    };
    go();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (state === 'failed') {
    return (
      <Page>
        <section className="container-narrow" style={{ textAlign: 'center', paddingTop: 80 }}>
          <h1 style={{ marginBottom: 12 }}>Plata nu a pornit</h1>
          <p className="muted" style={{ marginBottom: 24 }}>{errMessage}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <a href={'#/preview/' + sessionId} onClick={(e) => { e.preventDefault(); navigate('/preview/' + sessionId); }} className="btn btn-primary">
              Înapoi la previzualizare
            </a>
            <a href="mailto:salut@crayo.ro" className="btn btn-ghost">Scrie-ne</a>
          </div>
        </section>
      </Page>
    );
  }

  return (
    <Page>
      <section className="container-narrow" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 16, color: 'var(--muted)' }}>
          <ProcessingDot /> Te ducem la pagina de plată securizată Stripe...
        </div>
      </section>
    </Page>
  );
}

function ProcessingDot() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14">
      <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="24" pathLength="32">
        <animateTransform attributeName="transform" type="rotate" from="0 7 7" to="360 7 7" dur="1s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// --- /download/:orderId ---
// `orderId` is either a Stripe Checkout Session id (if Stripe redirected
// here with {CHECKOUT_SESSION_ID} before the webhook resolved it) OR a
// Crayo `ord_...` id. Forge's backend resolves both in GET /api/order/:oid
// per API.md, so the frontend treats them uniformly.
function DownloadPage({ orderId }) {
  const toast = useToast();
  const [order, setOrder] = useStateK(null);
  const [loadError, setLoadError] = useStateK(null);
  const [waitlistEmail, setWaitlistEmail] = useStateK('');
  const [downloadBusy, setDownloadBusy] = useStateK(false);
  const abortRef = useRefK(null);
  // Guards the GA4 purchase event so we fire exactly once even though the
  // poller may report `paid` repeatedly until downloadUrl resolves.
  const purchaseFiredRef = useRefK(false);

  // Poll until status transitions out of `pending`. Backend returns
  // `{status: 'pending', downloadUrl: null}` while the PDF is being
  // assembled; once `paid`, the signed URL becomes available.
  useEffectK(() => {
    let alive = true;
    let timeoutId = null;

    const tick = async () => {
      if (!alive) return;
      abortRef.current = new AbortController();
      try {
        const o = await getOrder(orderId, { signal: abortRef.current.signal });
        if (!alive) return;
        setOrder(o);
        // Stop polling once we reach a true terminal state. `paid` alone is
        // not enough: in the two-phase flow, payment lands BEFORE generation
        // + PDF assembly finish, so the order is `paid` while downloadUrl is
        // still null. Keep polling until both are true.
        if (!o) return;
        if (o.status === 'refunded' || o.status === 'failed') return;
        if (o.status === 'paid' && o.downloadUrl) return;
      } catch (err) {
        if (!alive) return;
        if (err && err.name === 'AbortError') return;
        // 410 (order.refunded / failed) per API.md returns a user-safe message;
        // show it and stop polling.
        if (err && err.code && (err.code.startsWith('order.') || err.code === 'http.410')) {
          setLoadError(err.message || 'Comanda nu mai este disponibilă.');
          return;
        }
        // Other network errors: keep polling quietly.
        // eslint-disable-next-line no-console
        console.warn('[crayo download] poll error', err);
      }
      timeoutId = setTimeout(tick, 2500);
    };
    tick();

    return () => {
      alive = false;
      if (abortRef.current) abortRef.current.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [orderId]);

  // Fire GA4 `purchase` exactly once on the first poll that returns
  // status='paid'. We do NOT fire Meta Pixel `Purchase` from the browser:
  // the server-side Conversions API has already reported it from the
  // Stripe webhook with the same event_id as `InitiateCheckout`, and a
  // second browser-side fire would defeat the dedup if the user lands
  // here on a different tab/device than the one that started checkout.
  useEffectK(() => {
    if (!order) return;
    if (order.status !== 'paid') return;
    if (purchaseFiredRef.current) return;
    purchaseFiredRef.current = true;
    const value = typeof order.totalPaid === 'number' ? order.totalPaid : 0;
    const tier = order.tier ?? 0;
    gaTrackEvent('purchase', {
      transaction_id: order.oid,
      value,
      currency: 'RON',
      items: [
        {
          item_id: 'tier_' + tier,
          item_name: 'Crayo coloring book ' + tier + ' pages',
          price: value,
          quantity: 1,
        },
      ],
    });
  }, [order]);

  // --- Render branches ---

  if (loadError) {
    return (
      <Page>
        <section className="container-narrow" style={{ textAlign: 'center', paddingTop: 80 }}>
          <h1 style={{ marginBottom: 12 }}>Comanda nu este disponibilă</h1>
          <p className="muted" style={{ marginBottom: 24 }}>{loadError}</p>
          <a href="mailto:salut@crayo.ro" className="btn btn-primary">Scrie-ne pentru ajutor</a>
        </section>
      </Page>
    );
  }

  // First-load shimmer. Very brief; the first poll response lands within
  // 150ms on a healthy backend.
  if (!order) {
    return (
      <Page>
        <section className="container-narrow" style={{ textAlign: 'center', paddingTop: 80 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 16, color: 'var(--muted)' }}>
            <ProcessingDot /> Verificăm statusul comenzii...
          </div>
        </section>
      </Page>
    );
  }

  // Payment failed terminal state. This is NOT a poll-quietly case; the
  // customer ended up here and deserves clear copy about what to do.
  if (order.status === 'failed') {
    return (
      <Page>
        <section className="container-narrow" style={{ textAlign: 'center', paddingTop: 80 }}>
          <h1 style={{ marginBottom: 12 }}>Plata nu a fost confirmată</h1>
          <p className="muted" style={{ marginBottom: 24, maxWidth: 520, margin: '0 auto 24px' }}>
            Banca sau cardul au respins plata. Nu ți s-a reținut nicio sumă. Poți încerca din nou cu alt card, sau să ne scrii dacă vrei ajutor.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <a href="#/create" onClick={(e) => { e.preventDefault(); navigate('/create'); }} className="btn btn-primary">Încearcă din nou</a>
            <a href="mailto:salut@crayo.ro" className="btn btn-ghost">Scrie-ne</a>
          </div>
        </section>
      </Page>
    );
  }

  if (order.status === 'refunded') {
    return (
      <Page>
        <section className="container-narrow" style={{ textAlign: 'center', paddingTop: 80 }}>
          <h1 style={{ marginBottom: 12 }}>Comandă rambursată</h1>
          <p className="muted" style={{ marginBottom: 24 }}>Am returnat suma pe cardul folosit la plată. Dacă nu apare în 5 zile lucrătoare, scrie-ne.</p>
          <a href="mailto:salut@crayo.ro" className="btn btn-primary">Scrie-ne</a>
        </section>
      </Page>
    );
  }

  // Pending: payment confirmed by Stripe but PDF not yet assembled.
  if (order.status === 'pending' || !order.downloadUrl) {
    return (
      <Page>
        <section className="container-narrow" style={{ textAlign: 'center', paddingTop: 64, paddingBottom: 72 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 24, opacity: 0.9 }}>
            <Icon.Star size={30} />
            <Icon.Balloon size={42} />
            <Icon.Star size={26} />
          </div>
          <h1 style={{ marginBottom: 16 }}>Mulțumim pentru comandă!</h1>
          <p style={{ color: 'var(--muted)', fontSize: 17, maxWidth: 520, margin: '0 auto 20px' }}>
            Tocmai asamblăm PDF-ul cu {order.pageCount || order.tier} pagini. Linkul de descărcare apare aici în câteva minute și îți ajunge și pe email.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--muted)' }}>
            <ProcessingDot /> Generăm PDF-ul...
          </div>
        </section>
      </Page>
    );
  }

  // status === 'paid' from here on.
  const pages = order.pageCount || order.tier;

  const downloadPDF = async () => {
    if (downloadBusy) return;
    setDownloadBusy(true);
    try {
      // Ask the backend for a fresh signed URL (15-min expiry per API.md)
      // rather than reusing what we polled; the one in state may have
      // been generated several minutes ago and could be near expiry.
      const fresh = await getOrder(orderId);
      const url = (fresh && fresh.downloadUrl) || order.downloadUrl;
      if (!url) {
        toast.push('Linkul nu a fost generat încă. Reîncearcă în câteva secunde.');
        return;
      }
      // Assign via anchor so the browser treats it as a save-as download,
      // not an in-tab navigation. window.open can be blocked by pop-up
      // policies; anchor+click is the safe pattern.
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.target = '_blank';
      a.download = 'crayo-cartea-mea.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.push('Descărcare pornită.');
    } catch (err) {
      toast.push((err && err.message) || 'Nu am putut deschide fișierul. Reîncearcă.');
    } finally {
      setDownloadBusy(false);
    }
  };

  // Waitlist submission. No backend endpoint for this yet (SHIP.md lists
  // it as future work); we keep the toast-only UX. Leaving a TODO for
  // when Andrei adds a /api/waitlist endpoint or pipes it to Resend.
  const waitlistSubmit = (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(waitlistEmail)) return;
    // TODO(backend): POST /api/waitlist when endpoint exists. For now the
    // confirmation is client-only so the button is not broken.
    toast.push('Mulțumim! Te anunțăm primii.');
    setWaitlistEmail('');
  };

  const resend = () => {
    // Forge owns a backend endpoint for resend of the download email.
    // If it is not wired yet, the user can still use the on-page button.
    toast.push('Linkul este vizibil mai sus. Dacă ai nevoie să ți-l trimitem din nou pe email, scrie-ne la salut@crayo.ro.');
  };

  return (
    <Page>
      <section style={{ paddingTop: 64, paddingBottom: 72 }}>
        <div className="container-narrow" style={{ textAlign: 'center' }}>
          {/* Celebratory doodles */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 24, opacity: 0.9 }}>
            <Icon.Star size={30} />
            <Icon.Balloon size={42} />
            <Icon.Star size={26} />
          </div>
          <h1 style={{ marginBottom: 16 }}>Cartea ta e gata!</h1>
          <p style={{ color: 'var(--muted)', fontSize: 17, maxWidth: 520, margin: '0 auto 36px' }}>
            Am trimis linkul și pe email.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 48 }}>
            <button onClick={downloadPDF} disabled={downloadBusy} className="btn btn-primary btn-lg" style={{ minWidth: 280 }}>
              {downloadBusy ? 'Se deschide...' : `Descarcă PDF (${pages} ${pages === 1 ? 'pagină' : 'pagini'})`}
            </button>
            <button onClick={resend} className="btn btn-ghost" style={{ fontSize: 13 }}>
              Retrimite linkul pe email
            </button>
            {order.email && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                Trimis la <strong style={{ color: 'var(--ink)' }}>{order.email}</strong>
              </div>
            )}
          </div>

          <div style={{ textAlign: 'left', display: 'grid', gap: 20 }}>
            {/* Print tips */}
            <div className="card">
              <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <Icon.Crayons /> Sfaturi pentru print
              </h3>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <li style={{ display: 'flex', gap: 10, fontSize: 15 }}><Icon.Check /> Hârtie A4, 90 g/m² sau mai groasă</li>
                <li style={{ display: 'flex', gap: 10, fontSize: 15 }}><Icon.Check /> Imprimare alb-negru, calitate înaltă</li>
                <li style={{ display: 'flex', gap: 10, fontSize: 15 }}><Icon.Check /> Marginile setate pe „Încapere în pagină" (Fit to page)</li>
                <li style={{ display: 'flex', gap: 10, fontSize: 15 }}><Icon.Check /> Pentru creioane colorate rezistente, folosește 120 g/m²</li>
              </ul>
            </div>

            {/* Waitlist */}
            <div className="card" style={{ background: '#FBF5EA', borderColor: 'var(--soft)' }}>
              <h3 style={{ marginBottom: 8 }}>Vrei și varianta tipărită, livrată acasă?</h3>
              <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 16 }}>
                Cărticică legată, hârtie groasă. Lasă-ne emailul și te anunțăm primul.
              </p>
              <form onSubmit={waitlistSubmit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input type="email" className="input" placeholder="email@exemplu.ro" value={waitlistEmail} onChange={(e)=>setWaitlistEmail(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
                <button type="submit" className="btn btn-primary">Anunță-mă</button>
              </form>
            </div>
          </div>

          {/* Soft repeat-purchase CTA. Goes back to /create, simple ghost
              button so it doesn't compete visually with the print tips
              card or the printed-book waitlist above. */}
          <div style={{ textAlign: 'center', marginTop: 28 }}>
            <a href="#/create" onClick={(e) => { e.preventDefault(); navigate('/create'); }} className="btn btn-ghost" style={{ fontSize: 14 }}>
              Începe o carte nouă
            </a>
          </div>
        </div>
      </section>
    </Page>
  );
}

Object.assign(window, { CheckoutPage, DownloadPage });
